import { Badge, Button, DateTile } from '@h2hspark/ui';
import { ArrowRight, ReceiptText } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { eventDetailPath } from '../event-discovery/constants';
import { formatTicketDateTime } from '../event-tickets/constants';

import {
  CANCELLED_BADGE_TONE,
  REGISTRATION_EVENT_LABEL,
  REGISTRATION_RECEIPT_LABEL,
  REGISTRATION_STATUS_BADGE_TONES,
  orderDetailPath,
  orderLine,
  savedCardLine,
  whenAndWhereLine,
} from './constants';
import type { MemberRegistrationSummary } from './types';

/**
 * **The rows of the member's own history, drawn once and rendered on two surfaces**
 * (`MEM-022` AC-3, AC-6).
 *
 * ## What changed on 2026-09-12, and why the row is horizontal now
 *
 * Sumit reviewed Phase 5 in the browser: *"some elements of your account does not match the
 * prototype styling."* This row was one of them, and it was not a matter of a colour. `Web App.dc
 * .html` §2.15 point 3 draws a **date tile · body · two buttons** row on one baseline —
 * `display:flex; gap:16px; align-items:center; border:1px solid var(--border-subtle);
 * border-radius:var(--radius-lg); padding:16px` — and what shipped was a stacked list item on a
 * divider, with the badge above the name and the two controls wrapped underneath. Every string on
 * it was right and the shape shared nothing with the design.
 *
 * So the row is now the prototype's, element for element:
 *
 *   - the **date tile** (`DateTile` variant `registration`, §3.1g) — the component the prototype
 *     uses on Discover, Your Events and the event header, added here rather than drawn inline;
 *   - the name, with its status badge beside it on one line;
 *   - `{when} · {venue}` in `--text-xs --text-muted`;
 *   - `Order {n} · {price} · {card}` in `--text-xs --text-subtle`;
 *   - **Receipt** (outline pill) and **Event** (filled pill, arrow after), right-aligned.
 *
 * ## Two places the prototype's row cannot be produced, and what is drawn instead
 *
 * **The city.** §2.15's second line is `{when} · {city}` and this one is `{when} · {venue}`.
 * There is no city to render: `ordered-event-port.ts`'s own header says so — *"The event entity
 * stores `venueName` and `venueAddress` and no separate city column"* — and inventing one by
 * parsing an address is the kind of derived fact that goes wrong in one row out of fifty. The
 * venue is the truer answer to the same question and is what `MEM-022` AC-4 asks for two screens
 * over, so the line keeps its place and changes its second half.
 *
 * **The badge's words.** §2.15 writes **Upcoming** / **Attended**. Ours are the served
 * `statusLabel` — *Registered*, *Paid*, *Refund pending*, *Refunded* — for the reason
 * `ORDER_STATUS_BADGES` gives in as many words: those are facts about the *event*, and *Attended*
 * in particular is the door's own record (`EVT-030`), which this payload does not read. A badge
 * here saying somebody attended would be a third answer to that question and the stalest of the
 * three. The **component** is the prototype's, in the prototype's slot; only the word differs,
 * which is the value-level call `docs/design/README.md` reserves. Named on the pull request as
 * *"Differs from spec — needs Sumit's yes"*, because he may want the event's tense instead.
 *
 * ## Why this is a component and not a block in each screen — unchanged
 *
 * `MEM-TKT-015` requires the web account page's *Your registrations* block and the PWA's
 * *Tickets & orders* screen to render **the same data**, and the story's Notes name the failure
 * mode exactly: *"AC-6 is a two-surface requirement that will drift unless it is one
 * implementation … Two renderers over one source; not two sources."*
 *
 * One hook is half of that — `useMyRegisteredEvents` is the single source. This is the other half.
 * `the-registrations-block-and-the-orders-list-agree.test.tsx` renders both surfaces from one
 * seeded cache entry and asserts the two regions are byte-identical.
 *
 * ## The two controls, and why the row itself is not a link
 *
 * It used to be a whole-row `<Link>` to the order detail, which is a target a free row does not
 * have. Sumit's ruling of 2026-09-03 names the replacement: **Receipt** and **Event**.
 *
 *   - **Event** is on every row, always. It goes to `/app/events/:eventId` — the page `MEM-015`
 *     AC-1 calls *"the page they return to once registered"* — which is where the ticket, the
 *     matches and the follow-up are reached from, and which after the evening says so rather than
 *     drawing the night's controls (`MEM-015` AC-7).
 *   - **Receipt** is drawn only where there is an order, and it opens `orderDetailPath` — the
 *     transaction record, whose own *View receipt* prints `MEM-022` AC-5's document. It keeps
 *     AC-3's *"full transaction details"* one tap from the list, which pointing straight at the
 *     printable receipt would not.
 *
 * Two sibling links rather than one nested in the other, because an anchor inside an anchor is
 * invalid and the browser un-nests it — which is how a control ends up navigating somewhere
 * nobody chose.
 *
 * ## The absences it draws, rather than defaults
 *
 * **No total on a row with no order.** `total` is `null` there, and `$0.00` would be a figure
 * computed from nothing — and on a registration an organizer entered at a *paid* event
 * (`EVT-026`) it would tell that member their ticket was free. The same holds for the card. The
 * order line renders the parts that exist and joins them with the one separator.
 *
 * **No amount arithmetic and no status logic anywhere here.** `total.amount` is the server's own
 * rendering by `formatCents`, and `statusLabel` and `cancelledLabel` are served — so a fifth
 * status needs no edit here and cannot arrive as an unlabelled badge.
 *
 * ## The date, and the zone it is not in
 *
 * Both the line and the tile are read in the **event's own timezone** — `formatTicketDateTime` for
 * the line, `DateTile`'s own `eventDateTileParts` for the tile — because
 * `docs/standards/time-and-timezone.md#display` forbids the reader's zone, and a second derivation
 * is how a member's ticket and their history row come to name two different days for one event.
 */
export function RegistrationSummaryPanel({
  registrations,
}: {
  readonly registrations: ReadonlyArray<MemberRegistrationSummary>;
}): React.ReactElement {
  return (
    <ul data-testid="order-summary-rows" className="flex flex-col gap-3">
      {registrations.map((row) => (
        <li
          key={`${row.eventId}:${row.orderNumber}`}
          className="flex flex-wrap items-center gap-4 rounded-lg border border-border-subtle p-4"
        >
          {/* §2.15's 52px tile. It renders nothing at all on an event with no date rather than a
              tile with a blank face, and the row's flex gap closes over it. */}
          {row.startsAt === null ? null : (
            <DateTile startsAt={row.startsAt} timezone={row.timezone} variant="registration" />
          )}

          <div className="min-w-0 flex-1 basis-[200px]">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="truncate font-brand text-md font-bold text-foreground">
                {row.eventName}
              </h3>
              <Badge variant={REGISTRATION_STATUS_BADGE_TONES[row.status]}>{row.statusLabel}</Badge>
              {/* The second badge, and only when the place was given up. Two facts, two badges:
                  a member who cancelled outside the refund window has a cancelled registration
                  standing on a paid order, and no single word is true of both. */}
              {row.cancelledLabel === null ? null : (
                <Badge variant={CANCELLED_BADGE_TONE}>{row.cancelledLabel}</Badge>
              )}
            </div>
            {/* When and where, in the event's own zone. Dropped entirely rather than shown as a
                dangling separator when the event carries neither. */}
            {renderWhenAndWhere(row)}
            <p className="mt-1 truncate text-xs text-text-subtle">
              {orderLine({
                orderNumber: row.orderNumber,
                price: row.total?.amount ?? null,
                card: row.card === null ? null : savedCardLine(row.card.brand, row.card.last4),
              })}
            </p>
          </div>

          <div className="flex flex-none gap-2">
            {row.orderId === null ? null : (
              <Button variant="outline-strong" size="pill" className="gap-1.5 px-[14px]" asChild>
                <Link to={orderDetailPath(row.orderId)}>
                  <ReceiptText className="h-[15px] w-[15px]" aria-hidden />
                  {REGISTRATION_RECEIPT_LABEL}
                </Link>
              </Button>
            )}
            <Button size="pill" className="gap-1.5" asChild>
              <Link to={eventDetailPath(row.eventId)}>
                {REGISTRATION_EVENT_LABEL}
                <ArrowRight className="h-[15px] w-[15px]" aria-hidden />
              </Link>
            </Button>
          </div>
        </li>
      ))}
    </ul>
  );
}

function renderWhenAndWhere(row: MemberRegistrationSummary): React.ReactElement | null {
  const line = whenAndWhereLine(
    formatTicketDateTime({ startsAt: row.startsAt, timezone: row.timezone }),
    row.venueName,
  );
  return line === null ? null : (
    <p className="mt-0.5 truncate text-xs text-muted-foreground">{line}</p>
  );
}
